/**
 * Secrets Settings - Manage shared & personal secrets per workspace.
 *
 * Terminology:
 *   - Shared secrets: whole workspace can use (extensions + flows), admin/owner write only
 *   - Personal secrets: only the owning user can use & write
 *
 * Security:
 *   - GET never returns plaintext/ciphertext (only is_set: true)
 *   - Edit dialogs are write-only (never pre-populate value)
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Key, Eye, EyeOff, Trash2, Plus, Loader2, AlertTriangle, Shield, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  SettingsSection,
  SettingsSectionRow,
  SETTINGS_SELECTED_SURFACE_CLASS,
} from '@/components/features/settings/settings-section'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { filterSecretNameSuggestions } from '@/lib/secret-name-suggestions'
import { useWorkspaceExtensions } from '@/hooks/use-workspace-extensions'
import { cn } from '@/lib/utils'
import { QUIET_ICON_BUTTON_DESTRUCTIVE_CLASS } from '@/lib/menu-chrome'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { fetchWithAuth } from '@/lib/api'
import { toast } from 'sonner'
import { testIds } from '@/lib/test-ids'
import { useWorkspace } from '@/providers/workspace-provider'
import { WorkspaceScopeSelector } from '@/components/layout/workspace-scope-selector'

interface Secret {
  id: string
  name: string
  description: string
  scope: 'shared' | 'personal'
  created_at: string
  updated_at: string
  last_used_at: string | null
  is_set: boolean
}

export function SecretsSettings() {
  const { workspaces, activeWorkspaceIds } = useWorkspace()

  // Single workspace whose secrets we manage (same PIP control as chat, exclusive mode)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Secrets state
  const [secrets, setSecrets] = useState<Secret[]>([])
  const [loading, setLoading] = useState(true)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editingSecret, setEditingSecret] = useState<Secret | null>(null)
  const [deleteConfirmSecret, setDeleteConfirmSecret] = useState<Secret | null>(null)
  const [saving, setSaving] = useState(false)

  const { extensions } = useWorkspaceExtensions(selectedOrgId)
  const enabledExtensionIds = useMemo(
    () => extensions.filter((e) => e.enabled).map((e) => e.extension_id),
    [extensions]
  )
  const nameSuggestions = useMemo(
    () =>
      filterSecretNameSuggestions({
        existingNames: secrets.map((s) => s.name),
        enabledExtensionIds,
      }),
    [secrets, enabledExtensionIds]
  )

  // Form state
  const [formName, setFormName] = useState('')
  const [formValue, setFormValue] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formScope, setFormScope] = useState<'shared' | 'personal'>('shared')
  const [showValue, setShowValue] = useState(false)

  /** Seed selection from active workspaces once the workspace list is available. */
  useEffect(() => {
    if (selectedOrgId || workspaces.length === 0) return
    const activeMatch = activeWorkspaceIds.find((id) => workspaces.some((w) => w.id === id))
    setSelectedOrgId(activeMatch ?? workspaces[0].id)
  }, [workspaces, activeWorkspaceIds, selectedOrgId])

  /**
   * Fetch secrets for the selected workspace only.
   * Do not fetch with orgId=null (JWT primary org) before seed — that race overwrites
   * the active-workspace list with an empty Hop (or other primary) result and hides
   * keys that chat BYOK is already using for the scoped workspace.
   */
  const fetchSecrets = useCallback(async (orgId: string, signal?: AbortSignal) => {
    setLoading(true)
    try {
      const qs = `?organizationId=${encodeURIComponent(orgId)}`
      const res = await fetchWithAuth(`/api/secrets${qs}`, signal ? { signal } : undefined)
      if (signal?.aborted) return
      const data = await res.json()
      if (signal?.aborted) return
      if (data.success) {
        setSecrets(data.secrets)
        setIsAdmin(Boolean(data.isOrgAdmin))
      }
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError')) return
      toast.error('Failed to load secrets')
    } finally {
      if (!signal?.aborted) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!selectedOrgId) return
    const ac = new AbortController()
    void fetchSecrets(selectedOrgId, ac.signal)
    return () => ac.abort()
  }, [fetchSecrets, selectedOrgId])

  const selectedOrg = workspaces.find((w) => w.id === selectedOrgId)

  /** Reset all form fields. */
  const resetForm = () => {
    setFormName('')
    setFormValue('')
    setFormDescription('')
    setFormScope(isAdmin ? 'shared' : 'personal')
    setShowValue(false)
  }

  const openAddDialog = (defaultScope?: 'shared' | 'personal') => {
    resetForm()
    if (defaultScope) setFormScope(defaultScope)
    setAddDialogOpen(true)
  }

  const openEditDialog = (secret: Secret) => {
    setFormName(secret.name)
    setFormValue('')
    setFormDescription(secret.description)
    setFormScope(secret.scope)
    setShowValue(false)
    setEditingSecret(secret)
  }

  const closeDialogs = () => {
    setAddDialogOpen(false)
    setEditingSecret(null)
    resetForm()
  }

  const handleSave = async () => {
    if (!formName || !/^[A-Z][A-Z0-9_]{0,127}$/.test(formName)) {
      toast.error('Secret name must be uppercase letters, digits, and underscores (max 128 chars), starting with a letter.')
      return
    }
    if (!editingSecret && !formValue) {
      toast.error('Secret value is required')
      return
    }

    setSaving(true)
    // Must pass organizationId on all mutations — selected workspace may differ from JWT org
    const qs = selectedOrgId ? `?organizationId=${encodeURIComponent(selectedOrgId)}` : ''
    try {
      if (editingSecret) {
        const body: Record<string, string> = {}
        if (formName !== editingSecret.name) body.name = formName
        if (formDescription !== (editingSecret.description || '')) body.description = formDescription
        if (formValue) body.value = formValue

        if (Object.keys(body).length === 0) {
          toast.error('No changes to save')
          setSaving(false)
          return
        }

        const res = await fetchWithAuth(`/api/secrets/${editingSecret.id}${qs}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        const data = await res.json()
        if (data.success) {
          toast.success(`Secret ${formName} updated`)
          closeDialogs()
          if (selectedOrgId) void fetchSecrets(selectedOrgId)
        } else {
          toast.error(
            res.status === 409
              ? `Secret ${formName} already exists`
              : (data.error || 'Failed to update secret')
          )
        }
      } else {
        const res = await fetchWithAuth(`/api/secrets${qs}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: formName, value: formValue, description: formDescription, scope: formScope }),
        })
        const data = await res.json()
        if (data.success) {
          toast.success(`Secret ${formName} created`)
          closeDialogs()
          if (selectedOrgId) void fetchSecrets(selectedOrgId)
        } else {
          toast.error(res.status === 409 ? `Secret ${formName} already exists` : (data.error || 'Failed to create secret'))
        }
      }
    } catch {
      toast.error('Failed to save secret')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (secret: Secret) => {
    try {
      const qs = selectedOrgId ? `?organizationId=${encodeURIComponent(selectedOrgId)}` : ''
      const res = await fetchWithAuth(`/api/secrets/${secret.id}${qs}`, { method: 'DELETE' })
      const data = await res.json()
      if (data.success) {
        toast.success(`Secret ${secret.name} deleted`)
        if (selectedOrgId) void fetchSecrets(selectedOrgId)
      } else {
        toast.error(data.error || 'Failed to delete secret')
      }
    } catch {
      toast.error('Failed to delete secret')
    } finally {
      setDeleteConfirmSecret(null)
    }
  }

  const sharedSecrets = secrets.filter(s => s.scope === 'shared')
  const personalSecrets = secrets.filter(s => s.scope === 'personal')

  return (
    <div className="space-y-6" data-testid={testIds.secretsSettings.tab}>
      {/* Same PIP selector as chat; single mode = one workspace's secrets at a time */}
      {workspaces.length > 1 && (
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium">Secrets workspace</p>
            <p className="text-xs text-muted-foreground">
              Same workspace PIP control as chat. Pick one workspace to manage its shared and personal
              secrets (not the same as multi-select browse/chat scope).
            </p>
          </div>
          <WorkspaceScopeSelector
            mode="single"
            scope={selectedOrgId ? [selectedOrgId] : null}
            onScopeChange={(scope) => {
              if (scope?.[0]) setSelectedOrgId(scope[0])
            }}
            fallbackScope={
              activeWorkspaceIds[0]
                ? [activeWorkspaceIds[0]]
                : workspaces[0]
                  ? [workspaces[0].id]
                  : null
            }
            label="Secrets workspace"
            description="Choose which workspace's secrets to view and edit."
            tooltip={
              selectedOrg
                ? `Managing secrets for ${selectedOrg.name}`
                : 'Select a workspace'
            }
            triggerClassName="shrink-0"
          />
        </div>
      )}

      {/* Encryption warning */}
      <SettingsSection
        variant="warning"
        icon={<AlertTriangle />}
        title="Encryption key rotation"
        description="Secrets are encrypted with your hosting environment's master key. If that key is rotated, all stored secrets must be re-entered."
      />

      <p className="text-xs text-muted-foreground">
        LLM provider keys also live on Workspaces &gt; Keys.
      </p>

      {/* Shared Secrets */}
      <SettingsSection
        icon={<Shield />}
        title="Shared secrets"
        description={
          <>
            Available to all members, flows, and extensions in this workspace.{' '}
            {isAdmin ? 'You can manage these as an admin.' : 'Read-only for your role.'}
          </>
        }
        actions={
          isAdmin ? (
            <Button
              onClick={() => openAddDialog('shared')}
              size="sm"
              variant="outline"
              data-testid={testIds.secretsSettings.addButton}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add secret
            </Button>
          ) : undefined
        }
      >
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : sharedSecrets.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No shared secrets configured yet.
            {isAdmin ? ' Use Add secret to create one.' : null}
          </p>
        ) : (
          <div className="space-y-2" data-testid={testIds.secretsSettings.list}>
            {sharedSecrets.map((secret) => (
              <SecretRow
                key={secret.id}
                secret={secret}
                canEdit={isAdmin}
                onEdit={() => openEditDialog(secret)}
                onDelete={() => setDeleteConfirmSecret(secret)}
              />
            ))}
          </div>
        )}
      </SettingsSection>

      {/* Personal Secrets */}
      <SettingsSection
        icon={<User />}
        title="Personal secrets"
        description="Only visible to you. Use for testing or personal API keys."
        actions={
          <Button onClick={() => openAddDialog('personal')} size="sm" variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add secret
          </Button>
        }
      >
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        ) : personalSecrets.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No personal secrets configured yet. Use Add secret to create one.
          </p>
        ) : (
          <div className="space-y-2">
            {personalSecrets.map((secret) => (
              <SecretRow
                key={secret.id}
                secret={secret}
                canEdit
                onEdit={() => openEditDialog(secret)}
                onDelete={() => setDeleteConfirmSecret(secret)}
              />
            ))}
          </div>
        )}
      </SettingsSection>

      {/* Add/Edit Dialog */}
      <Dialog open={addDialogOpen || editingSecret !== null} onOpenChange={(open) => !open && closeDialogs()}>
        <DialogContent data-testid={testIds.secretsSettings.addDialog}>
          <DialogHeader>
            <DialogTitle>{editingSecret ? 'Edit Secret' : 'Add Secret'}</DialogTitle>
            <DialogDescription>
              {editingSecret
                ? 'Rename, update the description, or replace the value. Leave value empty to keep existing.'
                : `Creating in workspace: ${selectedOrg?.name ?? '...'}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="secret-name">Name</Label>
              <Input
                id="secret-name"
                data-testid={testIds.secretsSettings.nameInput}
                placeholder="MY_API_KEY"
                value={formName}
                onChange={(e) => setFormName(e.target.value.toUpperCase())}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Uppercase letters, digits, and underscores only. Max 128 characters.
                {editingSecret
                  ? ' Renaming updates references - update any {{secrets.NAME}} usages to match.'
                  : ''}
              </p>
              {!editingSecret && nameSuggestions.length > 0 && (
                <div
                  className="space-y-1.5 pt-1"
                  data-testid={testIds.secretsSettings.nameSuggestions}
                >
                  <p className="text-xs font-medium text-foreground/80">Suggested names</p>
                  <p className="text-[11px] text-muted-foreground">
                    LLM keys and secrets for enabled extensions. Click to fill.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {nameSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.name}
                        type="button"
                        title={suggestion.description}
                        onClick={() => {
                          setFormName(suggestion.name)
                          if (!formDescription.trim()) {
                            setFormDescription(suggestion.description)
                          }
                        }}
                        className={cn(
                          'inline-flex items-center rounded-md border px-2 py-1 font-mono text-[11px] transition-colors',
                          formName === suggestion.name
                            ? cn(SETTINGS_SELECTED_SURFACE_CLASS, 'text-foreground')
                            : 'border-border/70 bg-muted/30 text-foreground/90 hover:border-primary/30 hover:bg-primary/10'
                        )}
                      >
                        {suggestion.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Scope toggle (only for new secrets by admins) */}
            {!editingSecret && isAdmin && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Scope</Label>
                  <p className="text-xs text-muted-foreground">
                    {formScope === 'shared'
                      ? 'Shared - all members and extensions can use this'
                      : 'Personal - only you can use this'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${formScope === 'personal' ? 'text-muted-foreground' : 'font-medium'}`}>
                    Shared
                  </span>
                  <Switch
                    data-testid={testIds.secretsSettings.scopeToggle}
                    checked={formScope === 'personal'}
                    onCheckedChange={(checked) => setFormScope(checked ? 'personal' : 'shared')}
                  />
                  <span className={`text-sm ${formScope === 'shared' ? 'text-muted-foreground' : 'font-medium'}`}>
                    Personal
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="secret-description">Description (optional)</Label>
              <Textarea
                id="secret-description"
                placeholder="e.g., Zoom S2S client ID for meeting transcript capture"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="secret-value">
                Value {editingSecret && '(leave empty to keep existing)'}
              </Label>
              <div className="relative">
                <Input
                  id="secret-value"
                  data-testid={testIds.secretsSettings.valueInput}
                  type={showValue ? 'text' : 'password'}
                  placeholder={editingSecret ? '(unchanged)' : 'Enter secret value'}
                  value={formValue}
                  onChange={(e) => setFormValue(e.target.value)}
                  className="pr-10 font-mono"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  data-testid={testIds.secretsSettings.valueToggle}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowValue(!showValue)}
                >
                  {showValue ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Max 8,192 bytes. Never logged or displayed after saving.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={closeDialogs} disabled={saving}>Cancel</Button>
            <Button
              data-testid={testIds.secretsSettings.saveButton}
              onClick={handleSave}
              disabled={saving || (!editingSecret && !formValue)}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingSecret ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmSecret !== null} onOpenChange={(open) => !open && setDeleteConfirmSecret(null)}>
        <AlertDialogContent data-testid={testIds.secretsSettings.deleteConfirm}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Secret</AlertDialogTitle>
            <AlertDialogDescription>
              Delete <code className="font-mono font-semibold">{deleteConfirmSecret?.name}</code>?
              Flows and extensions referencing{' '}
              <code className="font-mono text-xs">{'{{secrets.' + deleteConfirmSecret?.name + '}}'}</code> will stop resolving it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirmSecret && handleDelete(deleteConfirmSecret)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/** Reusable secret row for both shared and personal lists. */
function SecretRow({
  secret,
  canEdit,
  onEdit,
  onDelete,
}: {
  secret: Secret
  canEdit: boolean
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <SettingsSectionRow
      data-testid={testIds.secretsSettings.secretRow(secret.id)}
      className="flex items-center justify-between hover:bg-accent/40 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <code className="text-sm font-mono font-semibold">{secret.name}</code>
          <Badge variant="outline" className="text-xs text-muted-foreground font-medium">
            {secret.scope === 'shared' ? 'Shared' : 'Personal'}
          </Badge>
        </div>
        {secret.description && (
          <p className="text-xs text-muted-foreground">{secret.description}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">
          <Key className="h-3 w-3 inline mr-1 opacity-60" />
          {secret.last_used_at
            ? `Last used ${new Date(secret.last_used_at).toLocaleDateString()}`
            : 'Never used'}
        </p>
      </div>
      {canEdit && (
        <div className="flex items-center gap-2 ml-4">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className={cn('h-7 w-7', QUIET_ICON_BUTTON_DESTRUCTIVE_CLASS)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </SettingsSectionRow>
  )
}
